import { CandlestickData, SingleValueData, Time, UTCTimestamp } from 'lightweight-charts'
import { Security, SecurityExpression } from '../proto/generated/security'
import { Snapshot } from '../reducer/security.reducer'
import { getExchangeAndCodeFromKey, getExpressionSecurityKey, getSecurityKey, getSecuritySymbol } from './security'
import { ChartCandle } from '../components/chartV2/chart.typings'
import math, { availableFilters, filterDerivedUsageMap } from './math'
import { EXP_FILTER_DELIMTER } from '../assets/constants'
import { getInitialCandle } from './chart'


export const getOperatorRegex = () => new RegExp('[+\\-*/()^]', 'g')

export const operandEndingRegex = /[+\-\/\\%*]$/

export const getOperatorIndices = (expression: string) => {
    let result: [number, number][] = []
    let matches = Array.from(expression.matchAll(getOperatorRegex()))
    let start = 0
    matches.forEach((match) => {
        if (match['index'] !== undefined) {
            const index = match['index']
            result.push([start, Math.max(0, index - 1)])
            start = index + 1
        }
    })
    if (start < expression.length) {
        result.push([start, expression.length])
    }
    return result
}

export const getTopLevelOperandIndices = (expression: string): [number, number][] => {
    const stack: string[] = []
    let result = ''

    for (const char of expression) {
        if (char === '(') {
            stack.push(char)
            result += 'd' //any random char
        } else if (char === ')') {
            stack.pop()
            result += 'd'
        } else if (/[+\-*/^]/.test(char)) {
            if (stack.length === 0) {
                result += char
            } else {
                result += 'd'
            }
        } else {
            result += char
        }
    }
    return getOperatorIndices(result)
}

/** Top-level sum/difference legs (outside parentheses), e.g. `2*NSE:A+NSE:B` → two ranges. */
export const getTopLevelLegRanges = (expression: string): [number, number][] => {
    const trimmed = expression.trim()
    if (!trimmed.length) {
        return []
    }
    let depth = 0
    let termStart = 0
    const ranges: [number, number][] = []

    const pushRange = (end: number) => {
        if (end >= termStart) {
            const slice = trimmed.slice(termStart, end + 1).trim()
            if (slice.length) {
                ranges.push([termStart, end])
            }
        }
    }

    for (let i = 0; i < trimmed.length; i++) {
        const c = trimmed[i]
        if (c === '(') {
            depth++
        } else if (c === ')') {
            depth = Math.max(0, depth - 1)
        } else if (depth === 0 && c === '+') {
            pushRange(i - 1)
            termStart = i + 1
        } else if (depth === 0 && c === '-' && i > termStart) {
            const prev = trimmed[i - 1]
            if (/[)0-9a-zA-Z_:|]/.test(prev)) {
                pushRange(i - 1)
                termStart = i
            }
        }
    }
    pushRange(trimmed.length - 1)

    return ranges.length ? ranges : [[0, trimmed.length - 1]]
}

export const isExpressionMode = (symbol: string) => getOperatorRegex().test(symbol) || symbol.indexOf(EXP_FILTER_DELIMTER) !== -1


export const normalizeExpressionKeyFromUrl = (rawKey: string): string => {
    if (!rawKey || !rawKey.includes(' ')) {
        return rawKey
    }
    return rawKey.replace(/([A-Z0-9_]+:[^\s+]+) ([A-Z0-9_]+:)/g, '$1+$2')
}

export const getExpressionSymbols = (expression: string): { exchange: number, code: string }[] => {
    const operatorIndices = getOperatorIndices(expression)
    const symbols: { exchange: number, code: string }[] = []
    operatorIndices.forEach(([start, end]) => {
        if (end > start) {
            const [term] = expression.substring(start, end + 1).split(EXP_FILTER_DELIMTER)
            const [exchange, code] = getExchangeAndCodeFromKey(term.trim())
            if (!exchange || !code) {
                return
            }
            symbols.push({
                exchange: exchange,
                code
            })
        }
    })
    return symbols
}
export const getHighlightRange = (expression: string, selectionStart: number, selectionEnd: number, operatorIndices?: [number, number][]): [number, number] => {
    if (!operatorIndices) {
        operatorIndices = getTopLevelOperandIndices(expression)
    }

    if (operatorIndices.length === 0) return [expression.length, expression.length]

    let l = 0, r = operatorIndices.length - 1
    let leftIndex: number = -1, rightIndex: number = -1
    while ((leftIndex === -1 || rightIndex === -1) && l < operatorIndices.length && r >= 0) {
        const [l_start, l_end] = operatorIndices[l]
        const [r_start, r_end] = operatorIndices[r]
        if (l_start <= selectionStart && l_end + 1 >= selectionStart) {
            leftIndex = l_start
        } else {
            l += 1
        }

        if (r_start <= selectionEnd && r_end + 1 >= selectionEnd) {
            rightIndex = r_end
        } else {
            r -= 1
        }
    }

    if (rightIndex === -1) {
        return [expression.length, expression.length]
    }
    if (leftIndex === -1) {
        return [0, 0]
    }
    return [leftIndex, rightIndex + 1]
}

export const replaceNamesWithExpressions = (input: string, nameToExpression: Map<string, string>): string => {
    const keys = Array.from(nameToExpression.keys())
    return input.replace(new RegExp(keys.join('|'), 'g'), (match) => {
        return nameToExpression.get(match) ?? match
    })
}

function isParenthesisEndPair(leftIndex: number, rightIndex: number, tokens: string[]) {
    while (leftIndex >= 0 && rightIndex <= tokens.length - 1 && tokens[leftIndex] === '(' && tokens[rightIndex] === ')') {
        leftIndex--
        rightIndex++
    }
    if (leftIndex < 0 && rightIndex >= tokens.length) {
        return true
    }
    return false
}

export const cleanUpBadParenthesis = (expression: string, enclose: boolean = false): string => {
    if (!expression) return expression
    const pattern = /([A-Z]+:[A-Z]+[0-9]*)|([0-9]+)|([+\-*/()])/g

    const tokens: string[] | null = expression.match(pattern)
    const stk: number[] = []
    if (!tokens) {
        return expression
    }
    let removableIndex = -1
    tokens.forEach((token, index) => {
        if (removableIndex !== -1) {
            return
        }
        if (token === '(') {
            stk.push(index)
        } else if (token === ')') {
            if (isParenthesisEndPair(stk[stk.length - 1], index, tokens)) {
                removableIndex = stk[stk.length - 1]
            } else {
                stk.pop()
            }
        }
    })
    let updatedExpression = expression
    if (removableIndex !== -1) {
        const parenthesisToBeRemoved = removableIndex + 1
        updatedExpression = expression.slice(parenthesisToBeRemoved, -parenthesisToBeRemoved)
    }

    return enclose ? `(${updatedExpression})` : updatedExpression
}

export const convertExpressionToEvaluationExpression = (expression: string, securities: Security[]): {
    expression: string,
    context: Record<string, Security>
} => {
    let indices = getOperatorIndices(expression)
    let runningIndex = 0
    let evaluationExpression = ''
    let numberOfFilters = 0
    const context: Record<string, Security> = {}
    indices.forEach(([start, end]) => {
        if (end > start) {
            const term = expression.substring(start, end + 1)
            if (/[^0-9.]/.test(term)) {
                let [code, filter = ''] = term.trim().split(EXP_FILTER_DELIMTER)
                code = code.trim()
                filter = filter.trim()
                const sec = securities.find((security) => getSecurityKey(security.exchange, security.code) === code)!!
                let addedTerm = getExpressionSecurityKey(sec?.exchange, sec?.code)

                if (filter) {
                    let key = `security${++numberOfFilters}`
                    if (sec.derived && filterDerivedUsageMap[filter]) key += '_derived'
                    addedTerm = `${filter}(${key})`
                    context[key] = { ...sec }
                }
                evaluationExpression += expression.substring(runningIndex, start) + addedTerm
                runningIndex = end + 1
            }
        }
    })
    evaluationExpression += expression.substring(runningIndex, expression.length)
    return { expression: evaluationExpression, context }
}

export const convertExpressionToDisplayExpression = (expression: string, securities?: Security[]) => {
    try {
        if (!expression) return ''
        const secList = securities ?? []
        let indices = getOperatorIndices(expression)
        let runningIndex = 0
        let displayExpression = ''
        indices.forEach(([start, end]) => {
            if (end > start) {
                const term = expression.substring(start, end + 1)
                if (/[^0-9.]/.test(term)) {
                    let [code, filter = ''] = term.trim().split(EXP_FILTER_DELIMTER)
                    code = code.trim()
                    filter = filter.trim()
                    const sec = secList.find((security) => getSecurityKey(security.exchange, security.code) === code)
                    let addedTerm = sec ? getSecuritySymbol(sec.exchange, sec.symbol ?? '') : code

                    if (filter) {
                        addedTerm += `|${filter}`
                    }

                    displayExpression += expression.substring(runningIndex, start) + addedTerm
                    runningIndex = end + 1
                }
            }
        })
        displayExpression += expression.substring(runningIndex, expression.length)
        return displayExpression
    } catch (e) {
        console.log('home:', e)
        return ''
    }
}

export const getEmbeddedStrategyExpressionLegDisplayStrings = (expression: string, securities?: Security[]): string[] => {
    const trimmed = expression.trim()
    if (!trimmed.length) {
        return []
    }
    return getTopLevelLegRanges(trimmed).map(([start, end]) =>
        convertExpressionToDisplayExpression(trimmed.slice(start, end + 1), securities),
    )
}

export const convertDisplayExpressionToExpression = (displayExpression: string, securities: Security[]) => {
    let indices = getOperatorIndices(displayExpression)
    let runningIndex = 0
    let expression = ''
    indices.forEach(([start, end]) => {
        if (end > start) {
            const term = displayExpression.substring(start, end + 1)
            if (/[^0-9.]/.test(term)) {
                let [code, filter = ''] = term.trim().split(EXP_FILTER_DELIMTER)
                code = code.trim()
                filter = filter.trim()
                const sec = securities.find((security) => getSecuritySymbol(security?.exchange, security.symbol!!) === code)!!
                expression += displayExpression.substring(runningIndex, start) + getSecurityKey(sec?.exchange, sec?.code) + (filter ? `${EXP_FILTER_DELIMTER}${filter.toLowerCase()}` : '')
                runningIndex = end + 1
            }
        }
    })
    expression += displayExpression.substring(runningIndex, displayExpression.length)
    return expression
}

export const evaluateHistoricalData = (expression: string, data: Record<string, ChartCandle[]>, expressionContext: Record<string, Security>, referenceSecurities: Record<string, Security> = {}) => {
    const result: ChartCandle[] = [];
    const tokenIndexes: Record<string, number> = {};
    Object.keys(data).forEach((k) => {
        tokenIndexes[k] = 0;
    }, {} as Record<string, number>);
    
    const derivedKeyMap: Record<string, string[]> = {}
    Object.keys(expressionContext).forEach((k) => {
        const s = expressionContext[k]
        if (!s.derived || k.indexOf('derived') === -1) return
        const secKey = getSecurityKey(s.exchange, s.underlyingSecurityCode!)
        if (!derivedKeyMap[secKey]) derivedKeyMap[secKey] = []
        derivedKeyMap[secKey].push(k)
    })
    
    const lastCandle: Record<string, ChartCandle> = {};
    const currentCandles: Record<string, ChartCandle> = {};

    const tokens = Object.keys(data);
    const tokensLength = tokens.length;
    const keys: Array<keyof ChartCandle> = ['open', 'close'];
    while (true) {
        const timestamps = tokens.map(k => data[k][tokenIndexes[k]]).filter(v => v).map(v => v.time as UTCTimestamp);
        if (!timestamps.length) break;

        const currentTimestamp = Math.min(...timestamps)
        tokens.forEach((t) => {
            const index = tokenIndexes[t];
            if (data[t][index] && data[t][index].time === currentTimestamp) {
                currentCandles[t] = data[t][index];
                tokenIndexes[t]++;
                lastCandle[t] = currentCandles[t];
            } else if (lastCandle[t]) {
                const lastClose = lastCandle[t].close;
                currentCandles[t] = {
                    ...getInitialCandle(),
                    open: lastClose,
                    high: lastClose,
                    low: lastClose,
                    close: lastClose,
                    time: currentTimestamp as Time,
                    referencedSecurityCode: lastCandle[t].referencedSecurityCode,
                    lastTickTimestamp: lastCandle[t].lastTickTimestamp,
                }
            }
        });

        if (Object.keys(currentCandles).length === tokensLength) {
            let evalCandle: ChartCandle = {
                ...getInitialCandle(),
                time: currentTimestamp as Time
            }

            keys.forEach((key) => {
                const context: Record<string, any> = { ...expressionContext }
                for (let token of tokens) {
                    const candle = currentCandles[token];
                    context[token] = candle[key as keyof ChartCandle] as number
                    evalCandle.volume += candle.volume || 0
                    evalCandle.vwap += candle.vwap || 0
    
                    if (candle.referencedSecurityCode) {
                        const referenceSecurity = referenceSecurities[candle.referencedSecurityCode as string]
                        if (!referenceSecurity) return
    
                        const underlyingKey = getSecurityKey(referenceSecurity.exchange, referenceSecurity.underlyingSecurityCode!)
                        const derivedKeys = derivedKeyMap[underlyingKey] || []
                        derivedKeys.forEach((k) => {
                            context[k] = referenceSecurity
                        })
                    }
                }
    
                try {
                    //@ts-ignore
                    evalCandle[key] = math.evaluate(expression, context)
                } catch (e) {
                    console.log(e)
                }
            })
            evalCandle.high = Math.max(evalCandle.open, evalCandle.close)
            evalCandle.low = Math.min(evalCandle.open, evalCandle.close)
            result.push(evalCandle)
        }
    }
    return result
}


export const calculateSnapshotDetailsForExpression = (expression: string, securities: Security[], snapshots: Record<string, Snapshot>): {
    ltp: number,
    ltpChange: number,
    changePercentage: number,
    volume: number,
    referencedSecurity?: Security | undefined;
} | null => {
    const {
        expression: evaulationExpression,
        context
    } = convertExpressionToEvaluationExpression(expression, securities)

    const derivedKeyMap: Record<string, string[]> = {}
    Object.keys(context).forEach((k) => {
        const s = context[k]
        if (!s.derived || k.indexOf('derived') === -1) return
        const secKey = getSecurityKey(s.exchange, s.underlyingSecurityCode!)
        if (!derivedKeyMap[secKey]) derivedKeyMap[secKey] = []
        derivedKeyMap[secKey].push(k)
    })

    const ltp: Record<string, any> = { ...context }
    const previousClose: Record<string, any> = { ...context }
    const volume: Record<string, any> = { ...context }
    const securityKeyToSecurity: Record<string, Security> = {}

    securities.forEach((security) => {
        securityKeyToSecurity[getSecurityKey(security.exchange, security.code)] = security
    })

    for (let key in snapshots) {
        const sn = snapshots[key]
        const sec = securityKeyToSecurity[key]
        if (!sn || !sec) continue
        const expressionKey = getExpressionSecurityKey(sec.exchange, sec.code)
        const priceMultiplier = sec.priceMultiplier || 100
        ltp[expressionKey] = sn.lastTradedPrice / priceMultiplier
        previousClose[expressionKey] = sn.previousClose / priceMultiplier
        volume[expressionKey] = sn.volume

        if (sn.referencedSecurity?.code) {
            const referenceSecurity = sn.referencedSecurity
            if (!referenceSecurity) continue
            const underlyingKey = getSecurityKey(referenceSecurity.exchange, referenceSecurity.underlyingSecurityCode!)
            const derivedKeys = derivedKeyMap[underlyingKey] || []
            derivedKeys.forEach((k) => {
                ltp[k] = referenceSecurity
                previousClose[k] = referenceSecurity
                volume[k] = referenceSecurity
                context[k] = referenceSecurity
            })
        }
    }
    try {
        const ltpResult: number = math.evaluate(evaulationExpression, ltp)
        const previousCloseResult: number = math.evaluate(evaulationExpression, previousClose)
        const changeResult: number = ltpResult - previousCloseResult
        const volumeResult: number = math.evaluate(evaulationExpression, volume)
        const changePercentage = changeResult * 100 / (previousCloseResult || 1)
        return {
            ltp: ltpResult,
            ltpChange: changeResult,
            changePercentage: changePercentage,
            volume: volumeResult
        }
    } catch (error) {
        console.log('error extracting ltp, ltpchange, changePercentage, volume', error, evaulationExpression)
        return null
    }
}

export const getExpressionFromSaved = (expressions: SecurityExpression[] | undefined, expression: string): SecurityExpression | undefined => {
    return (expressions ?? []).find((exp) => (exp.expression === expression))
}

export const isExpressionValid = (expression: string): string => {
    let error: string = ''

    const operatorIndices = getOperatorIndices(expression)
    operatorIndices.forEach(([start, end]) => {
        if (end > start) {
            let [term, filter = ''] = expression.substring(start, end + 1).split(EXP_FILTER_DELIMTER)
            term = term.trim()
            filter = filter.trim()
            const [, code] = getExchangeAndCodeFromKey(term.trim())
            if (!code && /[^0-9\.]/.test(term)) {
                error = 'Invalid symbol'
                return
            }
            if (filter && availableFilters.indexOf(filter) === -1) error = 'Invalid filter in expression'
        }
    })
    return error
}

export function getExpressionSecuritiesCoefficients(expression: string) {
    var coefficients: any = {}

    var regex = /(-?\d*)\*?([a-zA-Z:_0-9]+)\*?(\d*)/g
    var match

    while ((match = regex.exec(expression)) !== null) {
        var coefficient: any = (match[1] ? match[1] === '-' ? -1 : Number(match[1]) ?? 1 : 1) * (match[3] ? Number(match[3]) ?? 1 : 1)
        var variable = match[2]
        coefficient = parseInt(coefficient, 10)
        if (coefficients[variable]) {
            coefficients[variable] += coefficient
        } else {
            coefficients[variable] = coefficient
        }
    }
    return coefficients
}
